/* Cart page only. Loaded from layout/theme.liquid when request.page_type == 'cart'.
 *
 * Two failures from Browser QA 2026-09-23 (warn-01, warn-03):
 *
 * 1. /cart stays a solid page-background with no cart. Prestige opts every page into
 *    cross-document view transitions, and Kaching navigates to /cart when /cart.js
 *    comes back HTTP 429 with cf-mitigated: challenge. Together the new document never
 *    finishes its first paint. The cart template opts out of the transition, and an
 *    inline script in the head (before app embeds) stops that redirect loop.
 *
 * 2. The drawer shows the line just added, and /cart immediately says the cart is empty.
 *    The drawer was filled from the add response. The cart page was filled from a GET
 *    that Shopify sometimes answers with an empty cart (Kaching's own client records
 *    this). This script re-reads /cart.js with a cache-bust, and if that count is not
 *    what the section rendered, replaces the section. If the section response is still
 *    wrong, it paints the lines from /cart.js into a normal checkout form. If a
 *    full-viewport layer is still blank, or Kaching is still showing the empty state
 *    while /cart.js has lines, the theme cart is uncovered. Kaching's nodes are not
 *    removed.
 *
 * The drawer add-to-checkout path does not load this file.
 */
(function () {
  'use strict';

  // Stop at the first populated read. A stale empty /cart.js is the one that arrives
  // first; a later read in the same burst is the cart the drawer already showed.
  function settleCartReads(reads) {
    var last = null;
    for (var i = 0; i < reads.length; i += 1) {
      var count = reads[i];
      if (typeof count !== 'number') continue;
      last = count;
      if (count > 0) return count;
    }
    return last;
  }

  function shouldReplaceSection(liquidCount, authoritativeCount) {
    if (typeof authoritativeCount !== 'number') return false;
    return authoritativeCount !== liquidCount;
  }

  // Uncover the theme cart when the viewport is a blank layer, or when Kaching has
  // settled on the empty state while /cart.js has lines.
  function shouldRevealThemeCart(authoritativeCount, viewportBlank, kachingShowsEmpty) {
    if (viewportBlank) return true;
    return authoritativeCount > 0 && kachingShowsEmpty === true;
  }

  // TSK532: on /cart the Kaching drawer sat over the page's own checkout button (the drawer's
  // checkout worked, the page's did not). This page IS the cart, so the drawer stays shut here,
  // hidden by the theme's reveal (CSS, never removed, LOG.md section 6) and the scroll lock lifted.
  // DEC116, 29 Sep 2026: never through Kaching's own close control. On /cart that control
  // navigates away (to its last-visited page, or to / when there is none), so clicking it made
  // /cart unreachable for every shopper, 4 of 4 cases measured on MAIN. hasCloseControl is kept
  // in the signature and deliberately ignored.
  function drawerAction(drawerOpen, hasCloseControl, checkoutCovered) {
    if (drawerOpen || checkoutCovered) return 'reveal';
    return 'none';
  }

  if (typeof window !== 'undefined') {
    window.BohemiaCartPage = {
      settleCartReads: settleCartReads,
      shouldReplaceSection: shouldReplaceSection,
      shouldRevealThemeCart: shouldRevealThemeCart,
      drawerAction: drawerAction
    };
  }

  if (typeof document === 'undefined') return;

  var REVEAL_AT = 1500;
  var READS = 3;
  var READ_GAP = 160;
  var FETCH_BUDGET = 700;

  var copy = { title: 'Cart', empty: 'Your cart is empty', checkout: 'Checkout', count: 0 };

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  function rootPath() {
    var root = window.Shopify && Shopify.routes && Shopify.routes.root;
    return root || '/';
  }

  function sectionEl() {
    return document.querySelector('.shopify-section--main-cart');
  }

  function readCopy() {
    var node = document.querySelector('[data-bohemia-cart-copy]');
    if (!node) return;
    try {
      var parsed = JSON.parse(node.textContent);
      if (parsed && typeof parsed.title === 'string') copy.title = parsed.title;
      if (parsed && typeof parsed.empty === 'string') copy.empty = parsed.empty;
      if (parsed && typeof parsed.checkout === 'string') copy.checkout = parsed.checkout;
      if (parsed && typeof parsed.count === 'number') copy.count = parsed.count;
    } catch (error) {}
  }

  function liquidCount() {
    var section = sectionEl();
    var node = section && section.querySelector('[data-bohemia-cart-count]');
    if (!node) return copy.count;
    var count = Number(node.getAttribute('data-bohemia-cart-count'));
    return Number.isFinite(count) ? count : copy.count;
  }

  function money(cents) {
    var format = window.Shopify && Shopify.formatMoney;
    var pattern = window.themeVariables && themeVariables.settings && themeVariables.settings.moneyFormat;
    if (typeof format === 'function' && pattern) {
      try { return format(cents, pattern); } catch (error) {}
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  async function fetchCartOnce() {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? window.setTimeout(function () { controller.abort(); }, FETCH_BUDGET) : 0;
    try {
      var response = await fetch(rootPath() + 'cart.js?bohemia_cart_page=' + Date.now(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      });
      if (!response.ok) return null;
      var cart = await response.json();
      if (!cart || typeof cart.item_count !== 'number' || !Array.isArray(cart.items)) return null;
      return cart;
    } catch (error) {
      return null;
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  }

  async function readCart() {
    var reads = [];
    var last = null;
    for (var i = 0; i < READS; i += 1) {
      var cart = await fetchCartOnce();
      reads.push(cart ? cart.item_count : null);
      if (cart) last = cart;
      if (settleCartReads(reads) > 0) return cart;
      if (i < READS - 1) await wait(READ_GAP);
    }
    return last;
  }

  function countIn(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector('[data-bohemia-cart-count]');
    if (!node) return null;
    var count = Number(node.getAttribute('data-bohemia-cart-count'));
    return Number.isFinite(count) ? count : null;
  }

  async function replaceSection(cart) {
    var section = sectionEl();
    if (!section || !section.id) return false;
    var id = section.id.replace(/^shopify-section-/, '');
    try {
      var response = await fetch(rootPath() + 'cart?section_id=' + encodeURIComponent(id) + '&bohemia_cart_section=' + Date.now(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) return false;
      var html = await response.text();
      var rendered = countIn(html);
      // Do not swap a populated cart.js for a section document that is still empty.
      if (cart.item_count > 0 && rendered === 0) return false;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('.shopify-section--main-cart') || doc.body.firstElementChild;
      if (!next) return false;
      section.replaceWith(next);
      readCopy();
      return liquidCount() === cart.item_count;
    } catch (error) {
      return false;
    }
  }

  function paintFallback(cart) {
    var section = sectionEl();
    var main = document.getElementById('main') || document.body;
    var host = document.createElement('div');
    host.className = 'section-spacing section-spacing--tight';
    host.setAttribute('data-bohemia-cart-count', String(cart.item_count));
    host.setAttribute('data-bohemia-cart-fallback', 'true');

    var container = document.createElement('div');
    container.className = 'container container--sm';
    var stack = document.createElement('div');
    stack.className = 'v-stack gap-6';

    var heading = document.createElement('h1');
    heading.className = 'h2';
    heading.textContent = copy.title;
    stack.appendChild(heading);

    if (!cart.item_count) {
      var empty = document.createElement('p');
      empty.textContent = copy.empty;
      stack.appendChild(empty);
    } else {
      var list = document.createElement('ul');
      list.className = 'v-stack gap-4';
      list.style.listStyle = 'none';
      list.style.margin = '0';
      list.style.padding = '0';
      cart.items.forEach(function (item) {
        var row = document.createElement('li');
        row.className = 'h-stack gap-4';
        var name = document.createElement('span');
        var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.name;
        name.textContent = item.product_title || item.title || '';
        if (item.quantity > 1) name.textContent += ' × ' + item.quantity;
        if (plan) name.textContent += ' (' + plan + ')';
        var price = document.createElement('span');
        price.textContent = money(item.final_line_price);
        row.appendChild(name);
        row.appendChild(price);
        list.appendChild(row);
      });
      stack.appendChild(list);

      var form = document.createElement('form');
      form.action = rootPath() + 'cart';
      form.method = 'post';
      var button = document.createElement('button');
      button.type = 'submit';
      button.name = 'checkout';
      button.className = 'button';
      button.textContent = copy.checkout + ' · ' + money(cart.total_price);
      form.appendChild(button);
      stack.appendChild(form);
    }

    container.appendChild(stack);
    host.appendChild(container);
    if (section) section.replaceChildren(host);
    else main.appendChild(host);
  }

  function viewportBlank() {
    var section = sectionEl();
    var sectionText = ((section && section.innerText) || '').replace(/\s+/g, ' ').trim();
    var el = document.elementFromPoint(Math.floor(window.innerWidth / 2), Math.floor(window.innerHeight / 2));
    // A short cart (the empty state, or a few lines) does not fill the viewport.
    // That is the page, not a stuck layer. Only a covering node with no text is.
    if (!el || el === document.body || el === document.documentElement) return sectionText.length < 12;
    var rect = el.getBoundingClientRect();
    var covers = rect.width >= window.innerWidth * 0.85 && rect.height >= window.innerHeight * 0.85;
    var text = (el.innerText || '').replace(/\s+/g, ' ').trim();
    return covers && text.length < 12;
  }

  function kachingShowsEmpty() {
    var nodes = document.querySelectorAll('kaching-cart-drawer, .kaching-cart');
    for (var i = 0; i < nodes.length; i += 1) {
      var text = (nodes[i].innerText || '').replace(/\s+/g, ' ');
      if (text.indexOf(copy.empty) !== -1) return true;
    }
    return false;
  }

  function revealThemeCart() {
    document.documentElement.classList.add('bohemia-cart-reveal');
    document.documentElement.classList.remove('kaching-body__cart-open');
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.hasAttribute('open') && typeof drawer.hide === 'function') {
      try { drawer.hide(); } catch (error) {}
    }
  }

  var KACHING_NODES = 'kaching-cart-drawer, .kaching-cart';
  var drawerPinned = false;

  function kachingDrawerOpen() {
    var cls = 'kaching-body__cart-open';
    return document.documentElement.classList.contains(cls)
      || Boolean(document.body && document.body.classList.contains(cls));
  }

  function pageCheckoutButton() {
    var buttons = document.querySelectorAll('button[name="checkout"], input[name="checkout"]');
    for (var i = 0; i < buttons.length; i += 1) {
      if (buttons[i].closest(KACHING_NODES)) continue;
      var rect = buttons[i].getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return buttons[i];
    }
    return null;
  }

  function checkoutCoveredByKaching() {
    var button = pageCheckoutButton();
    if (!button) return false;
    var rect = button.getBoundingClientRect();
    var x = Math.floor(rect.left + rect.width / 2);
    var y = Math.floor(rect.top + rect.height / 2);
    if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return false;
    var top = document.elementFromPoint(x, y);
    return Boolean(top && top !== button && !button.contains(top) && top.closest(KACHING_NODES));
  }

  var closing = false;
  function keepDrawerShut() {
    if (closing) return;
    var control = document.querySelector('.kaching-cart__close');
    var action = drawerAction(kachingDrawerOpen(), Boolean(control), checkoutCoveredByKaching());
    if (action === 'close') {
      closing = true;
      try { control.click(); } catch (error) {}
      window.setTimeout(function () {
        closing = false;
        if (drawerAction(kachingDrawerOpen(), false, checkoutCoveredByKaching()) === 'reveal') {
          drawerPinned = true;
          revealThemeCart();
          if (document.body) document.body.classList.remove('kaching-body__cart-open');
        }
      }, 400);
    } else if (action === 'reveal') {
      drawerPinned = true;
      revealThemeCart();
      if (document.body) document.body.classList.remove('kaching-body__cart-open');
    }
  }

  // If Kaching catches up to /cart.js, put its cart back. The class only exists
  // to stop a blank or empty shell covering the lines.
  function releaseRevealWhenKachingMatches(cart) {
    if (!cart || !cart.item_count || drawerPinned) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      tries += 1;
      var api = window.kachingCartApi;
      var shown = api && typeof api.getCartData === 'function' ? api.getCartData() : null;
      var matched = shown && shown.item_count === cart.item_count && !kachingShowsEmpty() && !viewportBlank();
      if (matched && !drawerPinned) {
        document.documentElement.classList.remove('bohemia-cart-reveal');
        window.clearInterval(timer);
      }
      if (tries > 12) window.clearInterval(timer);
    }, 250);
  }

  async function alignKaching(cart) {
    var api = null;
    for (var i = 0; i < 12; i += 1) {
      api = window.kachingCartApi;
      if (api && typeof api.refreshCart === 'function') break;
      await wait(100);
      api = null;
    }
    if (!api || typeof api.refreshCart !== 'function') return;
    try { await api.refreshCart(); } catch (error) {}
    var shown = typeof api.getCartData === 'function' ? api.getCartData() : null;
    if (shown && cart && shown.item_count === cart.item_count) return;
    try { await api.refreshCart(); } catch (error) {}
  }

  var running = null;

  async function sync() {
    if (running) return running;
    running = syncBody().finally(function () { running = null; });
    return running;
  }

  async function syncBody() {
    readCopy();
    var cart = await readCart();
    var authoritative = cart ? cart.item_count : null;
    if (cart && shouldReplaceSection(liquidCount(), authoritative)) {
      var replaced = await replaceSection(cart);
      if (!replaced) paintFallback(cart);
    }
    if (cart) {
      await alignKaching(cart);
      releaseRevealWhenKachingMatches(cart);
    }

    if (shouldRevealThemeCart(authoritative, viewportBlank(), cart && cart.item_count > 0 && kachingShowsEmpty())) {
      if (cart && cart.item_count > 0 && liquidCount() !== cart.item_count) paintFallback(cart);
      revealThemeCart();
    }
  }

  // Independent of the cart.js reads. A hung request must not leave the beige layer up.
  window.setTimeout(function () {
    if (!viewportBlank()) return;
    revealThemeCart();
  }, REVEAL_AT);

  function watchDrawer() {
    var observer = new MutationObserver(keepDrawerShut);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (document.body) observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    [0, 600, 1500, 3000, 6000].forEach(function (delay) { window.setTimeout(keepDrawerShut, delay); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { sync(); watchDrawer(); });
  else { sync(); watchDrawer(); }
  window.addEventListener('pageshow', function () { sync(); keepDrawerShut(); });
})();
